/**
 * Engage design system — Paybyrd-purple-led, Linear/Stripe-density,
 * fintech-serious. Matches SPECTRE's brand tokens at the top so when this
 * folds in, components inherit the host palette without surprise; layers
 * Engage-specific design refinements (typography scale, motion, depth) on
 * top.
 *
 * Three pillars:
 *
 *   1. **Tokens, not magic numbers**: every spacing / radius / color in
 *      components should reference a named token. Easier to audit and
 *      easier to swap a brand variable globally.
 *
 *   2. **Density**: cards have less padding than MUI defaults; data grids
 *      have tighter rows. Information density is a feature for ops users.
 *
 *   3. **Refined motion**: durations and easings codified, used by every
 *      animated component. Motion communicates state, never decorates.
 */

import { alpha, createTheme, type ThemeOptions } from "@mui/material/styles";

// Extend MUI's TypeText so `text.tertiary` is part of the theme typing —
// otherwise consumers using `sx={{ color: "text.tertiary" }}` get a TS
// error even though the value resolves correctly at runtime.
declare module "@mui/material/styles" {
  interface TypeText {
    tertiary: string;
  }
}

/* ── Brand color seed ────────────────────────────────────────────────── */

export const brand = {
  /** Paybyrd brand purple. Anchor of the SPA. */
  purple: "#6319f0",
  purpleDeep: "#4a14b8",
  purpleSoft: "#ede5ff",
  purpleHairline: "#dccfff",

  orange: "#f97316",
  orangeSoft: "#fff1e6",

  /** Navy used for hero gradients in transactional emails + login screen. */
  navy: "#0f0a1f",
  navyMid: "#2a1a5e",
} as const;

/* ── Neutral scale (warm, with a hint of purple in the highlights) ───── */

const neutral = {
  0: "#ffffff",
  25: "#fbfaff",
  50: "#f6f5f7",
  100: "#ece9f5",
  150: "#e0dcec",
  200: "#cfcad9",
  300: "#a09bb0",
  400: "#7c7a93",
  500: "#5b5874",
  600: "#3f3d56",
  700: "#26243a",
  800: "#171528",
  900: "#0f0a1f",
} as const;

/* ── Status colors (intent-bearing) ───────────────────────────────────── */

const status = {
  success: { main: "#10b981", soft: "#d1fae5", deep: "#047857" },
  warning: { main: "#f59e0b", soft: "#fef3c7", deep: "#b45309" },
  error:   { main: "#ef4444", soft: "#fee2e2", deep: "#b91c1c" },
  info:    { main: "#3b82f6", soft: "#dbeafe", deep: "#1d4ed8" },
} as const;

/** Mint green used by paybyrd.com for positive stat callouts (+12%, +€48k).
 *  Distinct from `success` so admin "approved!" toasts don't read identically
 *  to "your business grew" callouts. */
const accentMint = "#2dd4a8";
const accentMintDeep = "#0fa37a";

/* ── Typography scale (modular, perfect-fourth-ish) ───────────────────── */

const FONT_DISPLAY = '"Inter Display", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const FONT_BODY    = '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const FONT_MONO    = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

/* ── Motion tokens ────────────────────────────────────────────────────── */

export const motion = {
  duration: {
    instant: 80,
    fast: 160,
    base: 220,
    slow: 320,
    page: 420,
  },
  easing: {
    out: "cubic-bezier(0.16, 1, 0.3, 1)",         // strong out-quint, default for most enter
    inOut: "cubic-bezier(0.4, 0, 0.2, 1)",        // smooth, for size/position
    in: "cubic-bezier(0.7, 0, 0.84, 0)",          // for exit
    spring: "cubic-bezier(0.175, 0.885, 0.32, 1.275)",  // subtle bounce
  },
} as const;

/* ── Elevation (Linear-ish: subtle, layered shadows) ─────────────────── */

const shadows = {
  xs:  "0 1px 2px 0 rgba(15, 10, 31, 0.04)",
  sm:  "0 2px 4px -1px rgba(15, 10, 31, 0.05), 0 1px 2px 0 rgba(15, 10, 31, 0.04)",
  md:  "0 6px 16px -4px rgba(15, 10, 31, 0.08), 0 2px 4px -1px rgba(15, 10, 31, 0.05)",
  lg:  "0 18px 40px -16px rgba(15, 10, 31, 0.18), 0 4px 12px -4px rgba(15, 10, 31, 0.08)",
  glow: "0 0 0 4px rgba(99, 25, 240, 0.12)",
} as const;

/* ── Semantic surface tokens (light + dark) ───────────────────────────── */

const surfaceLight = {
  bg:           neutral[50],
  raised:       neutral[0],
  sunken:       neutral[100],
  border:       neutral[150],
  borderStrong: neutral[200],
} as const;

const textLight = {
  primary:   neutral[900],
  secondary: neutral[500],
  tertiary:  neutral[400],
  inverse:   neutral[0],
} as const;

const surfaceDark = {
  bg:           "#0c0917",     // app background, close-to-black with purple cast
  raised:       "#15112a",     // cards, dialogs
  sunken:       "#0f0a1f",     // inputs, scrollbars
  border:       "#241f3a",
  borderStrong: "#2f2a4a",
} as const;

const textDark = {
  primary:   "#f3f0fb",
  secondary: "#a09bb0",
  tertiary:  "#7c7a93",
  inverse:   neutral[900],
} as const;

/** What the app calls — defaults to the light values. `buildTheme(mode)` swaps to dark. */
const surface = surfaceLight;
const text = textLight;

/* ── The MUI theme ────────────────────────────────────────────────────── */

export type ColorMode = "light" | "dark";

function buildPalette(mode: ColorMode) {
  const s = mode === "dark" ? surfaceDark : surfaceLight;
  const t = mode === "dark" ? textDark : textLight;
  return {
    mode,
    primary:   { main: brand.purple, dark: brand.purpleDeep, light: brand.purpleSoft, contrastText: "#fff" },
    secondary: { main: brand.orange, light: brand.orangeSoft },
    success:   status.success,
    warning:   status.warning,
    error:     status.error,
    info:      status.info,
    background: { default: s.bg, paper: s.raised },
    text:      { primary: t.primary, secondary: t.secondary, tertiary: t.tertiary, disabled: t.tertiary },
    divider:   s.border,
    grey:      neutral as unknown as Record<string, string>,
  } as const;
}

function buildOptions(mode: ColorMode): ThemeOptions {
  const s = mode === "dark" ? surfaceDark : surfaceLight;
  const t = mode === "dark" ? textDark : textLight;
  return {
  palette: buildPalette(mode),

  shape: { borderRadius: 12 },

  typography: {
    fontFamily: FONT_BODY,
    fontWeightLight: 400,
    fontWeightRegular: 450,
    fontWeightMedium: 550,
    fontWeightBold: 650,
    h1: { fontFamily: FONT_DISPLAY, fontSize: "2.5rem",  lineHeight: 1.15, fontWeight: 650, letterSpacing: "-0.022em" },
    h2: { fontFamily: FONT_DISPLAY, fontSize: "2rem",    lineHeight: 1.2,  fontWeight: 650, letterSpacing: "-0.02em"  },
    h3: { fontFamily: FONT_DISPLAY, fontSize: "1.5rem",  lineHeight: 1.25, fontWeight: 600, letterSpacing: "-0.018em" },
    h4: { fontFamily: FONT_DISPLAY, fontSize: "1.25rem", lineHeight: 1.3,  fontWeight: 600, letterSpacing: "-0.015em" },
    h5: { fontFamily: FONT_DISPLAY, fontSize: "1.075rem",lineHeight: 1.35, fontWeight: 600, letterSpacing: "-0.01em"  },
    h6: { fontFamily: FONT_DISPLAY, fontSize: "0.95rem", lineHeight: 1.4,  fontWeight: 600, letterSpacing: "-0.005em" },
    body1:    { fontSize: "0.95rem", lineHeight: 1.55 },
    body2:    { fontSize: "0.875rem", lineHeight: 1.55 },
    caption:  { fontSize: "0.75rem",  lineHeight: 1.45, color: t.secondary },
    overline: {
      fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.16em",
      textTransform: "uppercase", color: t.tertiary, lineHeight: 1.4,
    },
    button: { textTransform: "none", fontWeight: 550, letterSpacing: 0 },
  },

  // Single shadow ramp — MUI requires 25 entries; we reuse our tokens at sane stops
  shadows: [
    "none",
    shadows.xs, shadows.xs, shadows.xs,
    shadows.sm, shadows.sm, shadows.sm, shadows.sm,
    shadows.md, shadows.md, shadows.md, shadows.md,
    shadows.md, shadows.md, shadows.md, shadows.md,
    shadows.lg, shadows.lg, shadows.lg, shadows.lg,
    shadows.lg, shadows.lg, shadows.lg, shadows.lg, shadows.lg,
  ] as ThemeOptions["shadows"],

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body": {
          fontFeatureSettings: '"cv11", "ss01", "ss03"',  // refined Inter glyphs
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(neutral[400], 0.3),
          borderRadius: 8,
          border: "2px solid transparent",
          backgroundClip: "content-box",
        },
        "*::-webkit-scrollbar-thumb:hover": { backgroundColor: alpha(neutral[400], 0.5) },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 9999,
          paddingInline: 18,
          paddingBlock: 8,
          transition: `background-color ${motion.duration.fast}ms ${motion.easing.out}, box-shadow ${motion.duration.fast}ms ${motion.easing.out}, transform ${motion.duration.fast}ms ${motion.easing.out}`,
          "&:active": { transform: "translateY(0.5px)" },
        },
        sizeSmall: { paddingInline: 14, paddingBlock: 5, fontSize: "0.825rem" },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: shadows.sm },
        },
        containedPrimary: {
          background: `linear-gradient(180deg, ${brand.purple} 0%, ${brand.purpleDeep} 100%)`,
          "&:hover": {
            background: `linear-gradient(180deg, ${brand.purpleDeep} 0%, ${brand.purpleDeep} 100%)`,
            boxShadow: `0 8px 16px -8px ${alpha(brand.purple, 0.55)}`,
          },
        },
        outlined: {
          borderColor: s.borderStrong,
          color: t.primary,
          "&:hover": { borderColor: t.tertiary, backgroundColor: s.sunken },
        },
        text: { "&:hover": { backgroundColor: s.sunken } },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: `background-color ${motion.duration.fast}ms ${motion.easing.out}`,
          "&:hover": { backgroundColor: s.sunken },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${s.border}`,
          boxShadow: shadows.xs,
          backgroundImage: "none",
        },
      },
    },

    MuiCardContent: {
      styleOverrides: { root: { padding: 20, "&:last-child": { paddingBottom: 20 } } },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: "none" } },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: "none" } },
    },

    MuiTextField: { defaultProps: { variant: "outlined", size: "small" } },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: s.raised,
          transition: `border-color ${motion.duration.fast}ms ${motion.easing.out}, box-shadow ${motion.duration.fast}ms ${motion.easing.out}`,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: s.borderStrong },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: t.tertiary },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: brand.purple,
            borderWidth: 1,
          },
          "&.Mui-focused": { boxShadow: shadows.glow },
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: { root: { fontWeight: 450, color: t.secondary } },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: 22,
          letterSpacing: "0.01em",
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: neutral[900],
          color: neutral[0],
          fontSize: "0.75rem",
          fontWeight: 500,
          padding: "6px 10px",
          borderRadius: 6,
          boxShadow: shadows.md,
        },
        arrow: { color: neutral[900] },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          boxShadow: shadows.lg,
          border: `1px solid ${s.border}`,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY,
          fontWeight: 600,
          fontSize: "1.1rem",
          letterSpacing: "-0.01em",
          paddingBottom: 8,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${s.border}`,
          fontSize: "0.875rem",
        },
        standardError:   { backgroundColor: status.error.soft,   borderColor: alpha(status.error.main, 0.25) },
        standardWarning: { backgroundColor: status.warning.soft, borderColor: alpha(status.warning.main, 0.25) },
        standardSuccess: { backgroundColor: status.success.soft, borderColor: alpha(status.success.main, 0.25) },
        standardInfo:    { backgroundColor: status.info.soft,    borderColor: alpha(status.info.main, 0.25) },
      },
    },

    // MuiDataGrid is from the @mui/x-data-grid package and isn't typed
    // into MUI core's Components map. We cast to bypass the strict check —
    // DataGrid still picks up these overrides at runtime via theme key.
    ...({
      MuiDataGrid: {
        defaultProps: { density: "comfortable" },
        styleOverrides: {
          root: {
            border: `1px solid ${s.border}`,
            borderRadius: 14,
            backgroundColor: s.raised,
            fontSize: "0.875rem",
            "--DataGrid-rowBorderColor": s.border,
            "--DataGrid-containerBackground": s.raised,
          },
          columnHeaders: {
            backgroundColor: s.sunken,
            borderBottom: `1px solid ${s.border}`,
            textTransform: "uppercase",
            fontSize: "0.7rem",
            letterSpacing: "0.08em",
            fontWeight: 600,
            color: t.tertiary,
          },
          row: {
            "&:hover": { backgroundColor: alpha(brand.purple, 0.025) },
          },
        },
      },
    } as Record<string, unknown>),

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          transition: `background-color ${motion.duration.fast}ms ${motion.easing.out}`,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderColor: s.border,
          backgroundImage: "none",
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 999, height: 6 } },
    },
  },
  };
}

export function buildTheme(mode: ColorMode) {
  return createTheme(buildOptions(mode));
}

/** Light theme by default — kept exported as `theme` for back-compat with
 *  any module that imports the value directly. */
export const theme = buildTheme("light");

/** Re-export tokens so components can compose from named values. The
 *  light/dark variants live under `tokens.light.*` and `tokens.dark.*`;
 *  for back-compat, `tokens.surface` / `tokens.text` resolve to the
 *  light values. Components that care about the active mode should use
 *  the MUI theme via `useTheme()` and read from `theme.palette` instead. */
export const tokens = {
  brand,
  neutral,
  status,
  surface,
  text,
  /** Mint accent used by paybyrd.com for positive stats. Distinct from
   *  `success` so admin success-toasts don't compete visually. */
  accent: { mint: accentMint, mintDeep: accentMintDeep },
  light: { surface: surfaceLight, text: textLight },
  dark:  { surface: surfaceDark,  text: textDark  },
  shadows,
  motion,
  font: { display: FONT_DISPLAY, body: FONT_BODY, mono: FONT_MONO },
} as const;
